import { Season } from '../auth/auth.service';

import mapboxgl from 'mapbox-gl';
import moment, { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const setPigeonsRoute = (
  map: mapboxgl.Map,
  isProductActive: boolean
) => {
  if (!isProductActive) return;

  const pigeonRouteData = localStorage.getItem('pigeon-route');
  const pigeonRoute = isNil(pigeonRouteData)
    ? null
    : JSON.parse(pigeonRouteData);

  if (isNil(pigeonRoute)) return;

  const start = [pigeonRoute.start.lng, pigeonRoute.start.lat];
  const end = [pigeonRoute.end.lng, pigeonRoute.end.lat];

  setPigeonsRouteLine(map, start, end);
  setPigeonsRouteFlags(map, start, end);
  zoomToPigeonsRoute(map, start, end);
};

export const setPigeonsRouteLine = (
  map: mapboxgl.Map,
  start: number[],
  end: number[]
) => {
  const routeId = 'pigeons-route';

  if (map.getLayer(routeId)) {
    map.removeLayer(routeId);
  }

  if (map.getSource(routeId)) {
    map.removeSource(routeId);
  }

  map.addSource(routeId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [start, end],
      },
    },
  });

  map.addLayer({
    id: routeId,
    type: 'line',
    source: routeId,
    paint: {
      'line-color': 'red',
      'line-width': 5,
      'line-opacity': 0.5,
    },
  });
};

export const setPigeonsRouteFlags = (
  map: mapboxgl.Map,
  start: number[],
  end: number[]
) => {
  const startFlagId = 'start-flag';
  const endFlagId = 'end-flag';
  const startFlagImage = 'start-flag-image';
  const endFlagImage = 'end-flag-image';

  if (map.getLayer(startFlagId)) {
    map.removeLayer(startFlagId);
  }

  if (map.getSource(startFlagId)) {
    map.removeSource(startFlagId);
  }

  if (map.getLayer(endFlagId)) {
    map.removeLayer(endFlagId);
  }

  if (map.getSource(endFlagId)) {
    map.removeSource(endFlagId);
  }

  map.loadImage('/assets/images/flag-white.png', (error, image) => {
    if (error) throw error;
    map.addImage(startFlagImage, image);

    map.addSource(startFlagId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: start,
        },
      } as any,
    });

    map.addLayer({
      id: startFlagId,
      type: 'symbol',
      source: startFlagId,
      layout: {
        'icon-image': startFlagImage,
        'icon-size': 1,
      },
    });
  });

  map.loadImage('/assets/images/flag-checkered-solid.png', (error, image) => {
    if (error) throw error;
    map.addImage(endFlagImage, image);

    map.addSource(endFlagId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: end,
        },
      } as any,
    });

    map.addLayer({
      id: endFlagId,
      type: 'symbol',
      source: endFlagId,
      layout: {
        'icon-image': endFlagImage,
        'icon-size': 1,
      },
    });
  });
};

export const zoomToPigeonsRoute = (
  map: mapboxgl.Map,
  start: number[],
  end: number[]
) => {
  const bounds: [number, number, number, number] = [
    Math.min(start[0], end[0]),
    Math.min(start[1], end[1]),
    Math.max(start[0], end[0]),
    Math.max(start[1], end[1]),
  ];
  map.fitBounds(bounds, {
    padding: { top: 60, bottom: 60, left: 60, right: 60 },
    duration: 1000,
  });
};

// REWORK //
export interface EppColorMappingDataset {
  value: number;
  rgb_code: string;
}

export type EppColorMappingStyle = {
  backgroundColor: string;
  color: string;
};

export type EppColorMappingResult = Partial<EppColorMappingStyle>;

export type EppColorMappings = Record<
  string,
  Record<string, EppColorMappingDataset[]>
>;

export type EppColorMappingPerParameterset = Record<string, string>;

const EPP_COLOR_MAPPING = {
  whiteRgbCode: 'ffffff',
  neutralGreenBackground: 'var(--epp-table-neutral-green-bg)',
  neutralBackground: 'var(--epp-table-neutral-bg)',
  neutralText: 'var(--epp-table-neutral-text)',
  lightText: '#111827',
  darkText: '#ffffff',
} as const;

const EPP_MAPPING_PARAMETER_FALLBACKS: Record<string, string> = {
  road_rst: 'tt',
  road_rst_min: 'tt',
  road_rst_max: 'tt',
  road_rst_F: 'tt_F',
  road_rst_min_F: 'tt_F',
  road_rst_max_f: 'tt_F',
  bridge_rst: 'tt',
  bridge_rst_min: 'tt',
  bridge_rst_max: 'tt',
  bridge_rst_F: 'tt_F',
  bridge_rst_min_F: 'tt_F',
  bridge_rst_max_F: 'tt_F',
  bike_rst: 'tt',
  bike_rst_min: 'tt',
  bike_rst_max: 'tt',
  bike_rst_F: 'tt_F',
  bike_rst_min_F: 'tt_F',
  bike_rst_max_F: 'tt_F',
  foot_rst: 'tt',
  foot_rst_min: 'tt',
  foot_rst_max: 'tt',
  foot_rst_F: 'tt_F',
  foot_rst_min_F: 'tt_F',
  foot_rst_max_F: 'tt_F',
  wing_rst: 'tt',
  wing_rst_min: 'tt',
  wing_rst_max: 'tt',
  wing_rst_F: 'tt_F',
  wing_rst_min_F: 'tt_F',
  wing_rst_max_F: 'tt_F',
  blade_rst: 'tt',
  panel_rst: 'tt',
  tx: 'tt',
  tn: 'tt',
  tx_F: 'tt_F',
  tn_F: 'tt_F',
  rail_tn: 'rail_tt',
  rail_tx: 'rail_tt',
  rail_tn_F: 'rail_tt_F',
  rail_tx_F: 'rail_tt_F',
  overhead_tn: 'overhead_tt',
  overhead_tx: 'overhead_tt',
  bridge_rsc: 'road_rsc',
  bike_rsc: 'road_rsc',
  foot_rsc: 'road_rsc',
  wing_rsc: 'road_rsc',
  blade_rsc: 'power_rsc',
  panel_rsc: 'power_rsc',
  i_di: 'i_sl',
  i_ein: 'i_sl',
  i_sl_wing: 'i_sl',
  ffx_kn: 'ff_kn',
  ff50_kn: 'ff_kn',
  ffx50_kn: 'ff_kn',
  ff100_kn: 'ff_kn',
  ffx100_kn: 'ff_kn',
};

export const getLegend = (
  parameter: string,
  parameterset: string,
  greenTableDesign: boolean,
  colorMappingPerParameterset: any,
  colorMappings: any
): Array<any> => {
  const legend = [];
  const imperialIndexText = ['i_co', 'i_gm', 'i_lr', 'i_st', 'i_he'];
  if (parameter.indexOf('i_') === 0) {
    // index
    const maxIndex =
      parameter === 'i_gp'
        ? 12
        : parameter === 'i_gc'
          ? 11
          : parameter === 'i_uv'
            ? 13
            : parameter === 'i_sl2'
              ? 9
              : parameter === 'i_blade_ice' ||
                  parameter === 'i_the' ||
                  parameter === 'i_hw' ||
                  parameter === 'i_fg' ||
                  parameter === 'i_sp_snow' ||
                  parameter === 'i_dust'
                ? 1
                : 4;
    const step = parameter === 'i_uv' ? 3 : 1;
    const parameterText =
      parameterset.indexOf('imperial') >= 0 &&
      imperialIndexText.indexOf(parameter) >= 0
        ? parameter + '_IMPERIAL'
        : parameter;
    for (let i = 0; i <= maxIndex; i += step) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          i,
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        value: indexSpan(parameter, i),
        text2: 'INDEX.' + parameterText + '.' + i,
      });
    }
  } else if (parameter.indexOf('ff', 0) === 0 && parameter.endsWith('_kn')) {
    const unit = 'kn';
    const values = colorMappingValues(
      parameterset,
      parameter,
      colorMappingPerParameterset,
      colorMappings
    );
    const texts = [];
    for (let i = 0; i < values.length; i++) {
      if (i < values.length - 1) {
        texts.push('<=' + values[i]);
      } else {
        texts.push('>' + values[i - 1]);
      }
    }
    for (let i = 0; i < values.length; i++) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          values[i],
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        value: texts[i] + unit,
      });
    }
  } else if (parameter === 'total_wave_hs') {
    const unit = 'm';
    const values = colorMappingValues(
      parameterset,
      parameter,
      colorMappingPerParameterset,
      colorMappings
    );
    const texts = [];
    for (let i = 0; i < values.length; i++) {
      if (i < values.length - 1) {
        texts.push('<=' + values[i]);
      } else {
        texts.push('>' + values[i - 1]);
      }
    }
    for (let i = 0; i < values.length; i++) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          values[i],
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        value: texts[i] + unit,
      });
    }
  } else if (
    parameter === 'tt' ||
    parameter === 'tt_F' ||
    parameter === 'tx' ||
    parameter === 'tn' ||
    parameter.indexOf('_rst') > 0 ||
    parameter.indexOf('_tt') > 0 ||
    parameter.indexOf('_tx') > 0 ||
    parameter.indexOf('_tn') > 0
  ) {
    const unit = parameter.indexOf('_F') >= 0 ? '°F' : '°C';
    const values = colorMappingValues(
      parameterset,
      parameter,
      colorMappingPerParameterset,
      colorMappings
    );
    const texts = [];
    for (let i = 0; i < values.length; i++) {
      if (i < values.length - 1) {
        texts.push('<=' + values[i]);
      } else {
        texts.push('>' + values[i - 1]);
      }
    }
    for (let i = 0; i < values.length; i++) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          values[i],
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        value: texts[i] + unit,
      });
    }
  } else if (parameter === 'panel_rsc' || parameter === 'blade_rsc') {
    for (let i = 0; i <= 2; i++) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          i,
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        text: 'POWER_CONDITION_SHORT.' + i,
        text2: 'POWER_CONDITION.' + i,
      });
    }
  } else if (
    parameter.indexOf('_rsc') > 0 ||
    parameter.indexOf('_condition') > 0
  ) {
    for (let i = 0; i <= 18; i++) {
      legend.push({
        style: colorMapping(
          parameterset,
          parameter,
          i,
          greenTableDesign,
          colorMappingPerParameterset,
          colorMappings
        ),
        text: 'CONDITION_SHORT.' + i,
        text2: 'CONDITION.' + i,
      });
    }
  } else if (parameter === 'sn') {
    const neutral = getNeutralBackgroundColor(greenTableDesign);
    const red = '#ffab91';

    legend.push({
      style: {
        backgroundColor: neutral,
        color: resolveForegroundColor(neutral),
      },
      value: '0 cm',
    });

    legend.push({
      style: {
        backgroundColor: red,
        color: resolveForegroundColor(red),
      },
      value: '> 0 cm',
    });
  } else if (parameter === 'sn_in') {
    const neutral = getNeutralBackgroundColor(greenTableDesign);
    const red = '#ffab91';

    legend.push({
      style: {
        backgroundColor: neutral,
        color: resolveForegroundColor(neutral),
      },
      value: '0 inch',
    });

    legend.push({
      style: {
        backgroundColor: red,
        color: resolveForegroundColor(red),
      },
      value: '> 0 inch',
    });
  }

  return legend;
};

export const colorMapping = (
  parameterset: string,
  parameter: string,
  value: number,
  greenTableDesign: boolean,
  colorMappingPerParameterset: EppColorMappingPerParameterset,
  colorMappings: EppColorMappings
): EppColorMappingResult => {
  const mapping = getMapping(
    parameterset,
    parameter,
    colorMappingPerParameterset,
    colorMappings
  );

  if (!mapping?.length || value === null || value === undefined) {
    return {};
  }

  const matchingDataset = mapping.find(dataset => value <= dataset.value);

  if (!matchingDataset) {
    return {};
  }

  return createColorMappingStyle(matchingDataset.rgb_code, greenTableDesign);
};

const createColorMappingStyle = (
  rgbCode: string,
  greenTableDesign: boolean
): EppColorMappingStyle => {
  const backgroundColor = resolveBackgroundColor(rgbCode, greenTableDesign);

  return {
    backgroundColor,
    color: resolveForegroundColor(backgroundColor),
  };
};

const resolveBackgroundColor = (
  rgbCode: string,
  greenTableDesign: boolean
): string => {
  const normalizedRgbCode = normalizeRgbCode(rgbCode);

  if (normalizedRgbCode === EPP_COLOR_MAPPING.whiteRgbCode) {
    return getNeutralBackgroundColor(greenTableDesign);
  }

  return `#${normalizedRgbCode}`;
};

const getNeutralBackgroundColor = (greenTableDesign: boolean): string => {
  return greenTableDesign
    ? EPP_COLOR_MAPPING.neutralGreenBackground
    : EPP_COLOR_MAPPING.neutralBackground;
};

const resolveForegroundColor = (backgroundColor: string): string => {
  if (isCssVariable(backgroundColor)) {
    return EPP_COLOR_MAPPING.neutralText;
  }

  return lightOrDark(backgroundColor) === 'light'
    ? EPP_COLOR_MAPPING.lightText
    : EPP_COLOR_MAPPING.darkText;
};

const normalizeRgbCode = (rgbCode: string): string => {
  return rgbCode.replace('#', '').trim().toLowerCase();
};

const isCssVariable = (color: string): boolean => {
  return color.startsWith('var(');
};

export const indexSpan = (
  parameter: string,
  value: number
): string | number => {
  if (parameter !== 'i_uv') {
    return value;
  }
  if (value < 3) {
    return '0-2';
  }
  if (value < 6) {
    return '3-5';
  }
  if (value < 8) {
    return '6-7';
  }
  if (value < 11) {
    return '8-10';
  }
  return '11-13';
};

export const lightOrDark = (color: string): string => {
  if (
    !color ||
    color === 'white' ||
    color === '#ffffff' ||
    color === 'transparent' ||
    color.startsWith('var(')
  ) {
    return 'light';
  }

  if (color === 'black' || color === '#000000') {
    return 'dark';
  }

  let r: number;
  let g: number;
  let b: number;

  if (color.match(/^rgb/)) {
    const rgb = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );

    if (!rgb) {
      return 'light';
    }

    r = Number(rgb[1]);
    g = Number(rgb[2]);
    b = Number(rgb[3]);
  } else {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);

    if (!result) {
      return 'light';
    }

    r = parseInt(result[1], 16);
    g = parseInt(result[2], 16);
    b = parseInt(result[3], 16);
  }

  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  return hsp > 180 ? 'light' : 'dark';
};
/*export const colorMapping = (
  parameterset: string,
  parameter: string,
  value: number,
  greenTableDesign: boolean,
  colorMappingPerParameterset: any,
  colorMappings: any
): any => {
  const neutral = greenTableDesign ? '#E7FCE3' : 'transparent';
  const mapping = getMapping(
    parameterset,
    parameter,
    colorMappingPerParameterset,
    colorMappings
  );
  if (mapping) {
    for (const dataset of mapping) {
      if (value <= dataset['value']) {
        const background =
          dataset['rgb_code'] === 'ffffff'
            ? neutral
            : '#' + dataset['rgb_code'];
        const foreground =
          lightOrDark(background) === 'light' ? 'black' : 'white';
        return { backgroundColor: background, color: foreground };
      }
    }
  }
  return {};
};*/

/*export const indexSpan = (
  parameter: string,
  value: number
): string | number => {
  if (parameter !== 'i_uv') {
    return value;
  }
  if (value < 3) {
    return '0-2';
  }
  if (value < 6) {
    return '3-5';
  }
  if (value < 8) {
    return '6-7';
  }
  if (value < 11) {
    return '8-10';
  }
  return '11-13';
};*/

/*
export const lightOrDark = (color: string): string => {
  if (
    !color ||
    color === 'white' ||
    color === '#ffffff' ||
    color === 'transparent'
  ) {
    return 'light';
  }
  if (color === 'black') {
    return 'dark';
  }
  // Variables for red, green, blue values
  let r;
  let g;
  let b;
  let hsp;

  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {
    // If RGB --> store the red, green, blue values in separate variables
    const rgb = color.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );
    r = rgb[1];
    g = rgb[2];
    b = rgb[3];
  } else {
    // If hex --> Convert it to RGB: http://gist.github.com/983661
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    r = parseInt(result[1], 16);
    g = parseInt(result[2], 16);
    b = parseInt(result[3], 16);
  }

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 180) {
    return 'light';
  } else {
    return 'dark';
  }
};
*/

export const colorMappingValues = (
  parameterset: string,
  parameter: string,
  colorMappingPerParameterset: any,
  colorMappings: any
): Array<number> => {
  const mapping = getMapping(
    parameterset,
    parameter,
    colorMappingPerParameterset,
    colorMappings
  );
  const values = [];
  if (mapping) {
    for (const dataset of mapping) {
      values.push(dataset['value']);
    }
  }
  return values;
};

export const getMapping = (
  parameterset: string,
  parameter: string,
  colorMappingPerParameterset: EppColorMappingPerParameterset,
  colorMappings: EppColorMappings
): EppColorMappingDataset[] | null => {
  const mappingId = colorMappingPerParameterset?.[parameterset];

  if (!mappingId) {
    return null;
  }

  const mapping = colorMappings?.[mappingId];

  if (!mapping) {
    return null;
  }

  const mappingParameter = mapping[parameter]
    ? parameter
    : mappingParameterFallback(parameter);

  if (!mappingParameter) {
    return null;
  }

  return mapping[mappingParameter] ?? null;
};

/*export const mappingParameterFallback = (parameter: string): string | null => {
  return EPP_MAPPING_PARAMETER_FALLBACKS[parameter] ?? null;
};*/

/*export const getMapping = (
  parameterset: string,
  parameter: string,
  colorMappingPerParameterset: any,
  colorMappings: any
): Array<any> => {
  if (colorMappingPerParameterset.hasOwnProperty(parameterset)) {
    const mappingId = colorMappingPerParameterset[parameterset];
    if (colorMappings.hasOwnProperty(mappingId)) {
      let mappingParameter;
      if (colorMappings[mappingId].hasOwnProperty(parameter)) {
        mappingParameter = parameter;
      } else {
        mappingParameter = mappingParameterFallback(parameter);
      }
      if (
        mappingParameter &&
        colorMappings[mappingId].hasOwnProperty(mappingParameter)
      ) {
        return colorMappings[mappingId][mappingParameter];
      }
    }
  }
  return null;
};*/

export const mappingParameterFallback = (parameter: string): string => {
  const mapping = {
    road_rst: 'tt',
    road_rst_min: 'tt',
    road_rst_max: 'tt',
    road_rst_F: 'tt_F',
    road_rst_min_F: 'tt_F',
    road_rst_max_f: 'tt_F',
    bridge_rst: 'tt',
    bridge_rst_min: 'tt',
    bridge_rst_max: 'tt',
    bridge_rst_F: 'tt_F',
    bridge_rst_min_F: 'tt_F',
    bridge_rst_max_F: 'tt_F',
    bike_rst: 'tt',
    bike_rst_min: 'tt',
    bike_rst_max: 'tt',
    bike_rst_F: 'tt_F',
    bike_rst_min_F: 'tt_F',
    bike_rst_max_F: 'tt_F',
    foot_rst: 'tt',
    foot_rst_min: 'tt',
    foot_rst_max: 'tt',
    foot_rst_F: 'tt_F',
    foot_rst_min_F: 'tt_F',
    foot_rst_max_F: 'tt_F',
    wing_rst: 'tt',
    wing_rst_min: 'tt',
    wing_rst_max: 'tt',
    wing_rst_F: 'tt_F',
    wing_rst_min_F: 'tt_F',
    wing_rst_max_F: 'tt_F',
    blade_rst: 'tt',
    panel_rst: 'tt',
    tx: 'tt',
    tn: 'tt',
    tx_F: 'tt_F',
    tn_F: 'tt_F',
    rail_tn: 'rail_tt',
    rail_tx: 'rail_tt',
    rail_tn_F: 'rail_tt_F',
    rail_tx_F: 'rail_tt_F',
    overhead_tn: 'overhead_tt',
    overhead_tx: 'overhead_tt',
    bridge_rsc: 'road_rsc',
    bike_rsc: 'road_rsc',
    foot_rsc: 'road_rsc',
    wing_rsc: 'road_rsc',
    blade_rsc: 'power_rsc',
    panel_rsc: 'power_rsc',
    i_di: 'i_sl',
    i_ein: 'i_sl',
    i_sl_wing: 'i_sl',
    ffx_kn: 'ff_kn',
    ff50_kn: 'ff_kn',
    ffx50_kn: 'ff_kn',
    ff100_kn: 'ff_kn',
    ffx100_kn: 'ff_kn',
  };
  return mapping.hasOwnProperty(parameter) ? mapping[parameter] : null;
};

export const hasLegend = (parameter: string): boolean => {
  const parametersWithLegend = [
    'tt',
    'tt_F',
    'tx',
    'tx_F',
    'tn',
    'tn_F',
    'sn',
    'sn_in',
    'road_rst',
    'road_rst_F',
    'bridge_rst',
    'bridge_rst_F',
    'bike_rst',
    'bike_rst_F',
    'foot_rst',
    'foot_rst_F',
    'blade_rst',
    'panel_rst',
    'road_rsc',
    'bridge_rsc',
    'bike_rsc',
    'foot_rsc',
    'blade_rsc',
    'panel_rsc',
    'i_he',
    'i_gm',
    'i_hr',
    'i_lr',
    'i_st',
    'i_th',
    'i_uv',
    'i_sl',
    'i_co',
    'i_ld',
    'i_blade',
    'rail_i_sl',
    'rail_tt',
    'rail_tt_F',
    'rail_tx',
    'rail_tx_F',
    'rail_tn',
    'rail_tn_F',
    'rail_condition',
    'i_sl2',
    'overhead_tt',
    'overhead_tt_F',
    'overhead_tx',
    'overhead_tx_F',
    'overhead_tn',
    'overhead_tn_F',
    'overhead_condition',
    'i_di',
    'wing_rst',
    'wing_rst_F',
    'wing_rsc',
    'wing_rst_max',
    'wing_rst_max_F',
    'wing_rst_min',
    'wing_rst_min_F',
    'ff_kn',
    'ffx_kn',
    'ff50_kn',
    'ffx50_kn',
    'ff100_kn',
    'ffx100_kn',
    'total_wave_hs',
];
  return (
    parameter.indexOf('i_') === 0 ||
    parametersWithLegend.indexOf(parameter) >= 0
  );
};

export const isProductActive = (
  isMaster: boolean,
  customerAlias: any,
  customerDocument: any,
  hasUserWebAccess: boolean,
  userMetaData: any,
  productType: string,
  productId?: number,
  alsoInThePast?: boolean,
  alsoOutOfSeason?: boolean,
  isUserAdminMode?: boolean
): boolean => {
  if (isMaster && !customerAlias && !productId) {
    return true;
  }

  if (!customerDocument) {
    return false;
  }
  if (!hasUserWebAccess) {
    return false;
  }
  if (
    productId !== undefined &&
    productId !== null &&
    !isUserAllowedToAccessToProduct(userMetaData, productId, isUserAdminMode)
  ) {
    return false;
  }

  if (
    customerDocument.hasOwnProperty(productType) &&
    customerDocument[productType].length > 0
  ) {
    for (const product of customerDocument[productType]) {
      if (
        product.active &&
        moment(product.contractStartDtg + 'T000000').isBefore(moment())
      ) {
        if (alsoInThePast) {
          if (productId) {
            if (product.id === productId) {
              return true;
            }
          } else {
            if (
              isUserAllowedToAccessToProduct(
                userMetaData,
                product.id,
                isUserAdminMode
              )
            ) {
              return true;
            }
          }
        }
        if (
          product.contractExpiryDtg === null ||
          moment(product.contractExpiryDtg + 'T000000').isAfter(
            moment().subtract(1, 'days')
          )
        ) {
          if (productId) {
            if (product.id === productId) {
              // return product.saison !== null ? this.isSeason(product.saison) : true;
              if (product.saison !== null) {
                if (
                  alsoOutOfSeason &&
                  isUserAllowedToAccessToProduct(
                    userMetaData,
                    product.id,
                    isUserAdminMode
                  )
                ) {
                  return true;
                }
                return isSeason(product.saison);
              } else {
                if (
                  isUserAllowedToAccessToProduct(
                    userMetaData,
                    product.id,
                    isUserAdminMode
                  )
                ) {
                  return true;
                }
              }
            }
          } else {
            if (
              alsoOutOfSeason &&
              isUserAllowedToAccessToProduct(
                userMetaData,
                product.id,
                isUserAdminMode
              )
            ) {
              return true;
            }
            if (product.saison !== null) {
              if (
                isSeason(product.saison) &&
                isUserAllowedToAccessToProduct(
                  userMetaData,
                  product.id,
                  isUserAdminMode
                )
              ) {
                return true;
              }
            } else if (
              isUserAllowedToAccessToProduct(
                userMetaData,
                product.id,
                isUserAdminMode
              )
            ) {
              return true;
            }
          }
        }
      }
    }
  }
  return false;
};

export const isUserAllowedToAccessToProduct = (
  userMetaData: any,
  productId: number,
  isUserAdminMode?: boolean,
  stationId?,
  ignoreAdminMode = false
): boolean => {
  if (isUserAdminMode && !ignoreAdminMode) {
    return true;
  }
  if (userMetaData && userMetaData.hasOwnProperty('online')) {
    if (userMetaData['online'].hasOwnProperty('product')) {
      if (userMetaData['online']['product'] === null) {
        return true;
      }
      for (const product of userMetaData['online']['product']) {
        if (product.hasOwnProperty('id')) {
          if (product['id'] === productId) {
            if (product.hasOwnProperty('active') && !product['active']) {
              return false;
            }
            if (stationId === undefined) {
              return true;
            }
            if (product.hasOwnProperty('station')) {
              if (product['station'] === null) {
                return true;
              }
              for (const station of product['station']) {
                if (station == stationId) {
                  return true;
                }
              }
            }
          }
        }
      }
      return false;
    }
  }
  return true;
};

export const isSeason = (season: Season): boolean => {
  // convert to moment
  const today: Moment = moment(moment().format('YYYY-MM-DD 00:00:00'));
  const start: Moment = moment({
    year: today.year(),
    month: Number(season.start.month) - 1,
    day: Number(season.start.day),
  });
  const end: Moment = moment({
    year: today.year(),
    month: Number(season.end.month) - 1,
    day: Number(season.end.day),
  });

  if (start.isSameOrBefore(end)) {
    // start <= end, z.B. 01.04.-30.10.
    return !(today.isBefore(start) || today.isAfter(end));
  }
  // start > end, z.B. 01.11.-30.04.
  if (today.isSameOrAfter(start) || today.isSameOrBefore(end)) {
    // z.B. today = 05.12. || today = 24.03
    return true;
  }
  return false;
};

export const MOMENT_LOCALE_MAP: Record<string, string> = {
  es: 'es',
  'es-ct': 'ca',
  de: 'de',
  nl: 'nl',
  fr: 'fr',
  us: 'en-us',
  'ch-de': 'de',
};

export const getLocaleFromLang = (lang: string) => {
  return MOMENT_LOCALE_MAP[lang.toLowerCase()] ?? 'en-us';
};
